import React, { useState } from "react";
import { Image as ImageIcon, Sparkle, Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ModelSelector } from "../components/ModelSelector";

const STYLES = [
  { id: "bold", label: "Bold YouTube" },
  { id: "minimal", label: "Minimal" },
  { id: "neon", label: "Neon" },
  { id: "cinematic", label: "Cinematic" },
];

export default function ThumbnailStudio() {
  const { setCredits } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("bold");
  const [model, setModel] = useState(null);
  const [compare, setCompare] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!prompt.trim()) { toast.error("Describe your thumbnail"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/thumbnail", { prompt, style, model, compare });
      if (typeof data?._credits_remaining === "number") setCredits(data._credits_remaining);
      setResult(data);
      toast.success(data.compare ? "Compared across models" : "Thumbnail ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate");
    } finally { setLoading(false); }
  };

  const download = (b64, label) => {
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${b64}`;
    a.download = `clipforge-thumbnail-${label || "img"}.png`;
    a.click();
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="thumbnail-studio">
      <p className="text-[#FF3B6B] text-sm font-mono">// THUMBNAIL AI</p>
      <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Forge a viral thumbnail</h1>
      <p className="text-white/50 mt-1">Pick Nano Banana, GPT Image 1, or A/B both</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 glass rounded-xl p-6 space-y-5">
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Describe the thumbnail</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} placeholder="e.g. shocked face, 'I QUIT MY JOB' bold text, money flying, dark background" className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm focus:border-[#FF3B6B] focus:outline-none resize-none" data-testid="thumb-prompt-input" />
          </div>
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Style</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {STYLES.map(s => (
                <button key={s.id} onClick={() => setStyle(s.id)} className={`py-2 rounded-md text-sm transition-all ${style === s.id ? "bg-[#FF3B6B] text-white" : "bg-white/5 border border-white/10 hover:bg-white/10"}`} data-testid={`thumb-style-${s.id}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <ModelSelector type="image" action="thumbnail" model={model} compare={compare} onChange={({ model: m, compare: c }) => { setModel(m); setCompare(c); }} />
          <button onClick={submit} disabled={loading} className="w-full btn-primary py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="generate-thumb-btn">
            {loading ? (compare ? "Comparing 2 image models (~30s)..." : "Forging...") : <><Sparkle weight="fill" size={16} /> {compare ? "Compare across models" : "Generate thumbnail"}</>}
          </button>
          <p className="text-xs text-white/40">~10-20s per model. Compare mode runs both in parallel.</p>
        </div>

        <div className="lg:col-span-7">
          {!result && !loading && (
            <div className="aspect-video glass rounded-xl flex items-center justify-center text-white/40 border-dashed" data-testid="thumb-placeholder">
              <div className="text-center"><ImageIcon size={50} className="mx-auto mb-3 text-white/20" /><p>Your thumbnail appears here</p></div>
            </div>
          )}
          {loading && (
            <div className="aspect-video shimmer bg-white/5 rounded-xl flex items-center justify-center text-white/60" data-testid="thumb-loading">
              <p>Forging your viral thumbnail...</p>
            </div>
          )}
          {result && !result.compare && result.image_base64 && (
            <div className="space-y-3" data-testid="thumb-result">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono px-2 py-1 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30">{result.model}</span>
              </div>
              <img src={`data:image/png;base64,${result.image_base64}`} alt="Generated thumbnail" className="w-full rounded-xl border border-white/10" />
              <button onClick={() => download(result.image_base64, result.model)} className="btn-primary px-5 py-2.5 rounded-lg font-medium flex items-center gap-2" data-testid="download-thumb-btn">
                <Download size={16} /> Download PNG
              </button>
            </div>
          )}
          {result?.compare && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="thumb-compare-grid">
              {result.variants.map((v) => (
                <div key={v.model} className="space-y-2" data-testid={`thumb-variant-${v.model}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono px-2 py-1 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30">{v.label}</span>
                  </div>
                  {v.image_base64 ? (
                    <>
                      <img src={`data:image/png;base64,${v.image_base64}`} alt={v.label} className="w-full rounded-xl border border-white/10" />
                      <button onClick={() => download(v.image_base64, v.model)} className="text-xs text-white/60 hover:text-white flex items-center gap-1.5">
                        <Download size={12} /> Download
                      </button>
                    </>
                  ) : (
                    <div className="aspect-video rounded-xl border border-red-500/30 bg-red-500/5 flex items-center justify-center p-4 text-center">
                      <p className="text-xs text-red-400">{v.error || "Failed to generate"}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
